import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/Base/Table";
import { DEFAULT_PAGINATION_SIZE } from "@/lib/const";
import { cn } from "@/lib/utils";
import {
  ColumnDef,
  PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  getSortedRowModel,
  getFilteredRowModel,
  RowSelectionState,
} from "@tanstack/react-table";
import { Dispatch, SetStateAction, useState, useMemo } from "react";
import { Spinner } from "@/components/Common/Spinner";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import { ArrowUpDownIcon, FilterIcon, DownloadIcon } from "lucide-react";
import { Button } from "@/components/Base/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Skeleton } from "@/components/Base/Skeleton";
import { Checkbox } from "@/components/Base/Checkbox";

export interface TableLayoutState {
  pagination?: Omit<PaginationState, "pageIndex">;
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  columnVisibility?: VisibilityState;
}

interface DataTableProps<TData, TValue, TExportData = TData> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  rowCount?: number;
  pagination?: PaginationState;
  setPagination?: Dispatch<SetStateAction<PaginationState>>;
  userDefaults?: TableLayoutState;
  factoryDefaults?: TableLayoutState;
  onLayoutSave?: (state: TableLayoutState) => void;
  onLayoutReset?: () => void;
  layoutSaveStrategy?: "automatic" | "user";
  loading?: boolean;
  sorting?: SortingState;
  setSorting?: Dispatch<SetStateAction<SortingState>>;
  headingContent?: React.ReactNode;
  showRecordRange?: boolean;
  onExportData?: () => Promise<TExportData[]> | TExportData[];
  exportFilename?: string;
  onRowClick?: (data: TData, event: React.MouseEvent) => void;
  onRowKeyDown?: (data: TData, event: React.KeyboardEvent) => void;
  rowClassName?: string;
  /** Applied to the table's outer wrapper. Use instead of reaching in with descendant selectors. */
  containerClassName?: string;
  /** Applied to the toolbar row above the table (heading content, Export, Columns). */
  toolbarClassName?: string;
  /**
   * Called with the full next visibility map whenever a column is shown or
   * hidden — the Columns menu's checkboxes and its Restore items alike. The
   * table still owns the state; this is purely a notification, so a caller
   * can persist the choice without the user clicking "Save Layout".
   */
  onColumnVisibilityChange?: (columnVisibility: VisibilityState) => void;
  showTotal?: boolean;
  hidePagination?: boolean;
  hideColumnsButton?: boolean;
  enableRowSelection?: boolean;
  getRowId?: (row: TData) => string;
  rowSelection?: RowSelectionState;
  setRowSelection?: Dispatch<SetStateAction<RowSelectionState>>;
}

/**
 *
 * @param root0
 * @param root0.columns
 * @param root0.data
 * @param root0.rowCount
 * @param root0.pagination
 * @param root0.setPagination
 * @param root0.userDefaults
 * @param root0.factoryDefaults
 * @param root0.loading
 * @param root0.sorting
 * @param root0.setSorting
 * @param root0.onLayoutSave
 * @param root0.onLayoutReset
 * @param root0.layoutSaveStrategy
 * @param root0.headingContent
 * @param root0.showRecordRange
 * @param root0.onExportData
 * @param root0.exportFilename
 */
export function DataTable<TData, TValue, TExportData = TData>({
  columns,
  data,
  rowCount,
  pagination,
  setPagination,
  userDefaults = {
    pagination: { pageSize: DEFAULT_PAGINATION_SIZE },
    sorting: [],
    columnFilters: [],
    columnVisibility: { id: false },
  },
  factoryDefaults = {
    pagination: { pageSize: DEFAULT_PAGINATION_SIZE },
    sorting: [],
    columnFilters: [],
    columnVisibility: { id: false },
  },
  loading,
  sorting,
  setSorting,
  onLayoutSave,
  onLayoutReset,
  layoutSaveStrategy = "user",
  headingContent,
  showRecordRange = false,
  onExportData,
  exportFilename = "export.csv",
  onRowClick,
  onRowKeyDown,
  rowClassName,
  containerClassName,
  toolbarClassName,
  onColumnVisibilityChange,
  showTotal = false,
  hidePagination = false,
  hideColumnsButton = false,
  enableRowSelection = false,
  getRowId,
  rowSelection: controlledRowSelection,
  setRowSelection: setControlledRowSelection,
}: DataTableProps<TData, TValue, TExportData>) {
  const [defaultPagination, setDefaultPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: userDefaults.pagination?.pageSize ?? 10,
  });
  const [defaultSorting, setDefaultSorting] = useState<SortingState>(
    userDefaults.sorting ?? []
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    userDefaults.columnFilters ?? []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    userDefaults.columnVisibility ?? {}
  );
  const [isExporting, setIsExporting] = useState(false);

  const [defaultRowSelection, setDefaultRowSelection] =
    useState<RowSelectionState>({});
  const rowSelection = controlledRowSelection ?? defaultRowSelection;
  const setRowSelection = setControlledRowSelection ?? setDefaultRowSelection;

  const selectionColumn: ColumnDef<TData, TValue> = useMemo(
    () => ({
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all rows on this page"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    }),
    []
  );

  const tableColumns = enableRowSelection
    ? [selectionColumn, ...columns]
    : columns;

  // Every path that changes visibility goes through here so `onColumnVisibilityChange`
  // can't be bypassed — TanStack's updater, and the Restore menu items below, which
  // would otherwise reset the columns without telling the caller that persists them.
  const applyColumnVisibility = (next: VisibilityState) => {
    setColumnVisibility(next);
    onColumnVisibilityChange?.(next);
  };

  const table = useReactTable({
    data,
    columns: tableColumns,
    getRowId,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination ? setPagination : setDefaultPagination,
    manualPagination: !!pagination,
    getPaginationRowModel:
      pagination || hidePagination ? undefined : getPaginationRowModel(),
    rowCount: rowCount ?? data?.length ?? 0,
    onSortingChange: setSorting ? setSorting : setDefaultSorting,
    manualSorting: !!sorting,
    getSortedRowModel: sorting ? undefined : getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: (updater) =>
      applyColumnVisibility(
        typeof updater === "function" ? updater(columnVisibility) : updater
      ),
    onRowSelectionChange: (updater) =>
      setRowSelection((old) =>
        typeof updater === "function" ? updater(old) : updater
      ),
    state: {
      pagination: pagination ? pagination : defaultPagination,
      sorting: sorting ? sorting : defaultSorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: pagination
      ? {
          pagination: {
            pageIndex: 0, //custom initial page index
            pageSize:
              userDefaults?.pagination?.pageSize ?? DEFAULT_PAGINATION_SIZE, //custom default page size
          },
        }
      : undefined,
  });

  const getRecordRangeText = () => {
    const currentPagination = pagination ? pagination : defaultPagination;
    const totalCount = rowCount ?? data.length;

    if (!data || data.length === 0) {
      return "Showing 0 of 0 records";
    }

    const startRecord =
      currentPagination.pageIndex * currentPagination.pageSize + 1;
    const endRecord = Math.min(
      (currentPagination.pageIndex + 1) * currentPagination.pageSize,
      totalCount
    );

    return `Showing ${startRecord}-${endRecord} of ${totalCount} records`;
  };

  const exportToCSV = async () => {
    if (!onExportData) return;

    setIsExporting(true);
    try {
      const exportData = await onExportData();
      if (!exportData || exportData.length === 0) return;

      const visibleColumns = table
        .getVisibleLeafColumns()
        .filter((column) => column.id !== "select");
      const headers = visibleColumns
        .map((column) => column.columnDef?.meta?.label ?? column.id)
        .join(",");

      const csvContent = exportData
        .map((row) => {
          return visibleColumns
            .map((column) => {
              const value = (row as Record<string, unknown>)[column.id];
              if (value === null || value === undefined) return "";
              const stringValue = String(value);
              return stringValue.includes(",")
                ? `"${stringValue.replace(/"/g, '""')}"`
                : stringValue;
            })
            .join(",");
        })
        .join("\n");

      const csv = `${headers}\n${csvContent}`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportFilename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setIsExporting(false);
    }
  };

  const calculateColumnTotals = () => {
    if (!showTotal || !data || data.length === 0) return {};

    const totals: Record<string, number | null> = {};
    const visibleColumns = table
      .getVisibleLeafColumns()
      .filter((column) => column.id !== "select");

    visibleColumns.forEach((column) => {
      const columnId = column.id;
      let sum = 0;
      let hasNumericValues = false;

      data.forEach((row) => {
        const value = (row as Record<string, unknown>)[columnId];
        if (typeof value === "number" && !isNaN(value)) {
          sum += value;
          hasNumericValues = true;
        }
      });

      totals[columnId] = hasNumericValues ? sum : null;
    });

    return totals;
  };

  return (
    <div className="w-full">
      {(headingContent || onExportData || !hideColumnsButton) && (
        <div className={cn("flex items-center py-4", toolbarClassName)}>
          {headingContent}
          <div className="flex-grow" />
          {onExportData && loading ? (
            <Skeleton className="mr-3 h-10 w-36" />
          ) : (
            onExportData && (
              <Button
                variant="outline"
                className="mr-3 w-36"
                onClick={exportToCSV}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Spinner />
                ) : (
                  <DownloadIcon className="mr-2 h-4 w-4" />
                )}
                {isExporting ? "Exporting..." : "Export CSV"}
              </Button>
            )
          )}

          {!hideColumnsButton &&
            (loading ? (
              <Skeleton className="ml-3 h-10 w-24" />
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="ml-3 w-24">
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(value)
                          }
                        >
                          <span className="mr-auto">
                            {column.columnDef?.meta?.label ?? column.id}
                          </span>
                          {column.getIsSorted() ? (
                            <ArrowUpDownIcon className="ml-0.5 h-3 w-5" />
                          ) : null}
                          {column.getIsFiltered() ? (
                            <FilterIcon className="ml-0.5 h-3 w-5" />
                          ) : null}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                  {((layoutSaveStrategy === "user" && onLayoutSave) ||
                    onLayoutReset) && <DropdownMenuSeparator />}
                  {layoutSaveStrategy === "user" && onLayoutSave && (
                    <>
                      <DropdownMenuItem
                        className="font-bold"
                        onClick={() =>
                          onLayoutSave({
                            pagination: {
                              pageSize:
                                pagination?.pageSize ?? DEFAULT_PAGINATION_SIZE,
                            },
                            sorting,
                            columnVisibility,
                            columnFilters,
                          })
                        }
                      >
                        Save Layout
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="font-bold"
                        onClick={() => {
                          if (setSorting) {
                            setSorting(userDefaults.sorting ?? []);
                          } else {
                            setDefaultSorting(userDefaults.sorting ?? []);
                          }
                          setColumnFilters(userDefaults.columnFilters ?? []);
                          applyColumnVisibility(
                            userDefaults.columnVisibility ?? {}
                          );
                        }}
                      >
                        Restore Last Saved
                      </DropdownMenuItem>
                    </>
                  )}
                  {onLayoutReset && (
                    <DropdownMenuItem
                      className="font-bold"
                      onClick={() => {
                        if (setSorting) {
                          setSorting(factoryDefaults.sorting ?? []);
                        } else {
                          setDefaultSorting(factoryDefaults.sorting ?? []);
                        }
                        setColumnFilters(factoryDefaults.columnFilters ?? []);
                        applyColumnVisibility(
                          factoryDefaults.columnVisibility ?? {}
                        );
                      }}
                    >
                      Restore Factory Default
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
        </div>
      )}
      <div className={cn("rounded-md border", containerClassName)}>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          {loading ? null : (
            <TableBody>
              {table.getRowModel().rows?.length ? (
                <>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      onClick={
                        onRowClick
                          ? (e) => onRowClick(row.original, e)
                          : undefined
                      }
                      onKeyDown={
                        onRowKeyDown
                          ? (e) => onRowKeyDown(row.original, e)
                          : undefined
                      }
                      className={rowClassName}
                      tabIndex={onRowClick || onRowKeyDown ? 0 : undefined}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={cell.column.columnDef.meta?.getCellClass?.(
                            cell.row
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {showTotal && (
                    <TableRow className="border-t-2 bg-muted/50 font-semibold">
                      {table
                        .getVisibleLeafColumns()
                        .filter((column) => column.id !== "select")
                        .map((column, index) => {
                          const totals = calculateColumnTotals();
                          const total = totals[column.id];
                          return (
                            <TableCell key={column.id}>
                              {index === 0 && "Total"}
                              {total !== null &&
                                total !== undefined &&
                                index > 0 &&
                                total}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                  )}
                </>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={tableColumns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
        {loading && <Skeleton className="h-24 w-full" />}
      </div>
      {!hidePagination && (
        <div className="grid grid-cols-3 items-center">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Show per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue
                  placeholder={table.getState().pagination.pageSize}
                />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 25, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-center space-x-2 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
          <div className="flex items-center justify-end text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
        </div>
      )}
      {showRecordRange && (
        <div className="flex justify-center py-2">
          <span className="text-sm text-muted-foreground">
            {getRecordRangeText()}
          </span>
        </div>
      )}
    </div>
  );
}
